/**
 * Vuex mutations for the "messages" module.
 *
 * Synchronous state writers for conversations, per-conversation messages,
 * typing/presence, drafts, and pagination/search bookkeeping. Async work
 * (API calls, socket events) lives in actions.js, which commits these
 * mutations with the results.
 */
export default {
  setInitialized(state, value) {
    state.hasInitialized = value;
  },
  setConversations(state, conversations) {
    // Kept in whatever order the API returned it in (per conversation_type
    // request — see fetchConversations action) rather than re-sorted here,
    // so the list matches the backend's own ordering exactly.
    state.conversations = [...conversations];
  },
  // Replaces just one conversation_type's slice of the list, leaving the
  // other type's rows untouched — used by fetchConversations so the 'user'
  // (DM) and 'group' requests can each land and render independently instead
  // of a full setConversations overwrite requiring both to have resolved
  // first (which would erase whichever type got there first while still
  // waiting on the other). requestType is the backend's query param value
  // ('user'/'group'), which the frontend models as 'dm'/'group' respectively.
  setConversationsByType(state, { requestType, conversations }) {
    const frontendType = requestType === 'group' ? 'group' : 'dm';
    const otherType = state.conversations.filter((c) => c.type !== frontendType);
    state.conversations = [...otherType, ...conversations];
  },
  setActiveConversationId(state, conversationId) {
    state.activeConversationId = conversationId;
  },
  /** Replace the full message list for one conversation (e.g. after loading its first page). */
  setMessages(state, { conversationId, messages }) {
    state.messagesByConversation = {
      ...state.messagesByConversation,
      [conversationId]: messages,
    };
  },
  /** Append a single message (e.g. an optimistic send) to its conversation's bucket. */
  addMessage(state, message) {
    const bucket = state.messagesByConversation[message.conversationId] || [];
    state.messagesByConversation = {
      ...state.messagesByConversation,
      [message.conversationId]: [...bucket, message],
    };
  },
  /** Swap a message in place by id — used both to confirm an optimistic send/edit and to revert on failure. */
  replaceMessage(state, { conversationId, tempId, message }) {
    const bucket = state.messagesByConversation[conversationId] || [];
    state.messagesByConversation = {
      ...state.messagesByConversation,
      [conversationId]: bucket.map((m) => (m.id === tempId ? message : m)),
    };
  },
  removeMessages(state, { conversationId, messageIds }) {
    const idSet = new Set(messageIds);
    const bucket = state.messagesByConversation[conversationId] || [];
    state.messagesByConversation = {
      ...state.messagesByConversation,
      [conversationId]: bucket.filter((m) => !idSet.has(m.id)),
    };
  },
  /** Update a conversation's last-message preview/timestamp and bump it to the top of the list, like a new message arriving. */
  updateConversationPreview(state, { conversationId, preview, thumbnailUrl = '', isLink = false, icon = null, at, incrementUnread }) {
    const existing = state.conversations.find((c) => c.id === conversationId);
    if (!existing) return;
    const updated = {
      ...existing,
      lastMessagePreview: preview,
      lastMessageThumbnailUrl: thumbnailUrl,
      lastMessageIsLink: isLink,
      lastMessagePreviewIcon: icon,
      lastMessageAt: at,
      unreadCount: incrementUnread ? existing.unreadCount + 1 : existing.unreadCount,
    };
    // Bump to the top of the list, like WhatsApp/Telegram — a conversation
    // that just got a new message (sent or received) is the most recent.
    state.conversations = [updated, ...state.conversations.filter((c) => c.id !== conversationId)];
  },
  // Companion to updateConversationPreview, used to fix up the preview after
  // a local delete rather than a new message — unlike that mutation, this
  // does NOT bump the conversation to the top of the list or touch
  // lastMessageAt/unreadCount, since deleting a message isn't new activity.
  refreshConversationPreview(state, { conversationId, preview, thumbnailUrl = '', isLink = false, icon = null }) {
    state.conversations = state.conversations.map((c) =>
      c.id === conversationId
        ? {
            ...c,
            lastMessagePreview: preview,
            lastMessageThumbnailUrl: thumbnailUrl,
            lastMessageIsLink: isLink,
            lastMessagePreviewIcon: icon,
          }
        : c
    );
  },
  clearUnread(state, conversationId) {
    state.conversations = state.conversations.map((c) =>
      c.id === conversationId ? { ...c, unreadCount: 0 } : c
    );
  },
  /** Record the unread count a conversation had right before it was opened (clearUnread zeroes the live value). */
  setUnreadSnapshot(state, { conversationId, count }) {
    state.unreadSnapshotByConversation = {
      ...state.unreadSnapshotByConversation,
      [conversationId]: count,
    };
  },
  /** Zero the unread count on every conversation ("mark all chats as read"). */
  clearAllUnread(state) {
    state.conversations = state.conversations.map((c) => ({ ...c, unreadCount: 0 }));
  },
  /** Force a conversation's unread count to at least 1 ("mark as unread"), without touching an already-nonzero count. */
  markUnread(state, conversationId) {
    state.conversations = state.conversations.map((c) =>
      c.id === conversationId ? { ...c, unreadCount: c.unreadCount > 0 ? c.unreadCount : 1 } : c
    );
  },
  // Removes conversations entirely (the chat-list row itself, plus all its
  // per-conversation state) — unlike clearConversationMessages, which only
  // empties the messages but keeps the row.
  removeConversations(state, conversationIds) {
    const idSet = new Set(conversationIds);
    const omit = (obj) => Object.fromEntries(Object.entries(obj).filter(([id]) => !idSet.has(id)));

    state.conversations = state.conversations.filter((c) => !idSet.has(c.id));
    if (idSet.has(state.activeConversationId)) state.activeConversationId = null;
    state.messagesByConversation = omit(state.messagesByConversation);
    state.messagesSkipByConversation = omit(state.messagesSkipByConversation);
    state.hasMoreMessagesByConversation = omit(state.hasMoreMessagesByConversation);
    state.isLoadingMoreMessagesByConversation = omit(state.isLoadingMoreMessagesByConversation);
    state.draftsByConversation = omit(state.draftsByConversation);
    state.typingByConversation = omit(state.typingByConversation);
    state.pendingContacts = omit(state.pendingContacts);
  },
  clearConversationMessages(state, { conversationId, keepStarred = false }) {
    const remaining = keepStarred
      ? (state.messagesByConversation[conversationId] || []).filter((m) => m.starred)
      : [];
    state.messagesByConversation = {
      ...state.messagesByConversation,
      [conversationId]: remaining,
    };
    state.conversations = state.conversations.map((c) =>
      c.id === conversationId
        ? {
            ...c,
            lastMessagePreview: remaining.length ? c.lastMessagePreview : '',
            lastMessageSenderName: remaining.length ? c.lastMessageSenderName : '',
            lastMessageThumbnailUrl: remaining.length ? c.lastMessageThumbnailUrl : '',
            lastMessageIsLink: remaining.length ? c.lastMessageIsLink : false,
            lastMessagePreviewIcon: remaining.length ? c.lastMessagePreviewIcon : null,
          }
        : c
    );
  },
  /** Update a conversation by id if it's already in the list, otherwise prepend it as new. */
  upsertConversation(state, conversation) {
    const exists = state.conversations.some((c) => c.id === conversation.id);
    if (exists) {
      state.conversations = state.conversations.map((c) =>
        c.id === conversation.id ? { ...c, ...conversation } : c
      );
      return;
    }
    state.conversations = [conversation, ...state.conversations];
  },
  // Patches in the participant's "anyone can message me" flag once fetched
  // (see fetchParticipantMessageSettings in actions.js) — never present on
  // the participant sub-doc conversations/pendingContacts start out with,
  // only on the fuller GET /api/users/username/:username response.
  setParticipantIsAnyoneCanMessage(state, { conversationId, isAnyoneCanMessage }) {
    if (state.conversations.some((c) => c.id === conversationId)) {
      state.conversations = state.conversations.map((c) =>
        c.id === conversationId
          ? { ...c, participant: { ...c.participant, isAnyoneCanMessage } }
          : c
      );
      return;
    }
    const pending = state.pendingContacts[conversationId];
    if (!pending) return;
    state.pendingContacts = {
      ...state.pendingContacts,
      [conversationId]: { ...pending, participant: { ...pending.participant, isAnyoneCanMessage } },
    };
  },
  // Patches a user's presence wherever they show up — unlike
  // setParticipantIsAnyoneCanMessage above, this is keyed by userId (not
  // conversationId), since one presence event can affect a DM's single
  // `participant`, that same user's entry in any group's `participants[]`,
  // a not-yet-started chat's pendingContacts entry (getActiveConversation
  // synthesizes the thread header from this until the first message is
  // sent), and a "Search People" row.
  setParticipantPresence(state, { userId, presence }) {
    state.conversations = state.conversations.map((c) => {
      if (c.participant?.id === userId) {
        return { ...c, participant: { ...c.participant, presence } };
      }
      if (Array.isArray(c.participants) && c.participants.some((p) => p.id === userId)) {
        return {
          ...c,
          participants: c.participants.map((p) => (p.id === userId ? { ...p, presence } : p)),
        };
      }
      return c;
    });

    Object.keys(state.pendingContacts).forEach((conversationId) => {
      const pending = state.pendingContacts[conversationId];
      if (pending.participant?.id === userId) {
        state.pendingContacts = {
          ...state.pendingContacts,
          [conversationId]: { ...pending, participant: { ...pending.participant, presence } },
        };
      }
    });

    state.peopleSearchResults = state.peopleSearchResults.map((p) =>
      p.id === userId ? { ...p, presence } : p
    );
  },
  setPendingContact(state, { conversationId, participant }) {
    state.pendingContacts = {
      ...state.pendingContacts,
      [conversationId]: { participant },
    };
  },
  clearPendingContact(state, conversationId) {
    if (!(conversationId in state.pendingContacts)) return;
    const next = { ...state.pendingContacts };
    delete next[conversationId];
    state.pendingContacts = next;
  },
  setDraft(state, { conversationId, text }) {
    // Drop the entry entirely once empty rather than keeping an empty
    // string around — keeps "does a draft exist" checks (e.g. the
    // conversation list preview) a simple truthy lookup.
    if (!text) {
      if (!(conversationId in state.draftsByConversation)) return;
      const next = { ...state.draftsByConversation };
      delete next[conversationId];
      state.draftsByConversation = next;
      return;
    }
    state.draftsByConversation = {
      ...state.draftsByConversation,
      [conversationId]: text,
    };
  },
  setTyping(state, { conversationId, isTyping }) {
    state.typingByConversation = {
      ...state.typingByConversation,
      [conversationId]: isTyping,
    };
  },
  setStatus(state, status) {
    state.status = status;
  },
  setError(state, error) {
    state.error = error;
  },
  setUnreadMessagesCount(state, count) {
    state.unreadMessagesCount = count;
  },
  /** Merge in the next page of conversations (infinite scroll) and re-sort the combined list by recency. */
  // Merges by id rather than blindly concatenating — offset-based pagination
  // (skip=N) can resurface a conversation already on an earlier page if it
  // gets new activity between page fetches (it shifts position server-side,
  // so the same row can land in both the already-loaded page and this new
  // one). Without deduping, that appends a second entry with the same id,
  // which collides with the first on Vue's :key and corrupts the list render
  // for both the duplicate and whatever else got appended alongside it.
  appendConversations(state, conversations) {
    // Merges by id rather than blindly concatenating — offset-based
    // pagination (skip=N) can resurface a conversation already on an earlier
    // page if it gets new activity between page fetches, which would
    // otherwise append a second entry with the same id (colliding with the
    // first on Vue's :key). Kept in API order (existing rows first, brand
    // new ones from this page appended after, matching Map's insertion-order
    // iteration) rather than re-sorted, to match setConversations above.
    const merged = new Map(state.conversations.map((c) => [c.id, c]));
    conversations.forEach((c) => merged.set(c.id, c));
    state.conversations = [...merged.values()];
  },
  /** Patch conversation-list search/pagination state; each field is only touched when provided. */
  setConversationsMeta(state, { search, skipByType, hasMoreByType }) {
    if (search !== undefined) state.conversationsSearch = search;
    if (skipByType) state.conversationsSkipByType = { ...state.conversationsSkipByType, ...skipByType };
    if (hasMoreByType) state.hasMoreConversationsByType = { ...state.hasMoreConversationsByType, ...hasMoreByType };
  },
  setLoadingMoreConversations(state, value) {
    state.isLoadingMoreConversations = value;
  },
  setPeopleSearchResults(state, results) {
    state.peopleSearchResults = results;
  },
  appendPeopleSearchResults(state, results) {
    state.peopleSearchResults = [...state.peopleSearchResults, ...results];
  },
  /** Patch people-search pagination state; each field is only touched when provided. */
  setPeopleSearchMeta(state, { offset, hasMore }) {
    if (offset !== undefined) state.peopleSearchOffset = offset;
    if (hasMore !== undefined) state.hasMorePeopleSearch = hasMore;
  },
  setLoadingMorePeopleSearch(state, value) {
    state.isLoadingMorePeopleSearch = value;
  },
  setSearchingPeople(state, value) {
    state.isSearchingPeople = value;
  },
  setAllUsersResults(state, results) {
    state.allUsersResults = results;
  },
  appendAllUsersResults(state, results) {
    state.allUsersResults = [...state.allUsersResults, ...results];
  },
  /** Patch all-users pagination state; each field is only touched when provided. */
  setAllUsersMeta(state, { offset, hasMore }) {
    if (offset !== undefined) state.allUsersOffset = offset;
    if (hasMore !== undefined) state.hasMoreAllUsers = hasMore;
  },
  setLoadingMoreAllUsers(state, value) {
    state.isLoadingMoreAllUsers = value;
  },
  setSearchingAllUsers(state, value) {
    state.isSearchingAllUsers = value;
  },
  prependMessages(state, { conversationId, messages }) {
    const bucket = state.messagesByConversation[conversationId] || [];
    // Merge by id (existing optimistic/sent messages keep their spot) and
    // resort by time rather than assuming the server's page order — avoids
    // duplicating or misordering messages when a page is fetched twice.
    const byId = new Map(bucket.map((m) => [m.id, m]));
    messages.forEach((m) => byId.set(m.id, m));
    state.messagesByConversation = {
      ...state.messagesByConversation,
      [conversationId]: Array.from(byId.values()).sort((a, b) => a.createdAt - b.createdAt),
    };
  },
  /** Patch a conversation's message-history pagination state; each field is only touched when provided. */
  setMessagesMeta(state, { conversationId, skip, hasMore }) {
    if (skip !== undefined) {
      state.messagesSkipByConversation = { ...state.messagesSkipByConversation, [conversationId]: skip };
    }
    if (hasMore !== undefined) {
      state.hasMoreMessagesByConversation = { ...state.hasMoreMessagesByConversation, [conversationId]: hasMore };
    }
  },
  setLoadingMoreMessages(state, { conversationId, value }) {
    state.isLoadingMoreMessagesByConversation = { ...state.isLoadingMoreMessagesByConversation, [conversationId]: value };
  },
  setLoadingActiveMessages(state, value) {
    state.isLoadingActiveMessages = value;
  },
  setDmAllowFrom(state, value) {
    state.dmAllowFrom = value;
  },
  setDmSettingsSaving(state, value) {
    state.isDmSettingsSaving = value;
  },
};
